import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getRFXQuestionsForRequest, getUserByUsername, loadRFXRequest, putRFXQuestion } from 'gql';
import { RFIRequestForm, RFIType } from 'interfaces/RFIRequestForm';
import { RFXQuestion } from 'interfaces/RFXQuestionModel';
import { AppDispatch, RootState } from 'store';
import { UIInput } from 'types/uiTypes';
import { convertSystemErrorToString } from 'util/errorUtils';
import {
  createSupplierRFIParticipationQuestion,
  createSupplierRFIQuestion,
  getNonReplyAllQuestions,
  hasNoPendingQuestionAndDocuments,
  sortQuestionsByCreationTime
} from 'util/questionUtils';
import { getRFXRequestIncludedVendor } from 'util/RFXUtils';
import { convertRFXRequestToRFIRequestForm } from 'util/transformers';

import { createRFIReplyContent } from '../common/rfiActions';
import { OnSendRFIMessageUIInput, PendingQuestionProps } from '../common/rfiTypes';
import { NEW_RFI_REQUEST_FORM } from '../rfiWizardSlice/constants';

export const SLICE_NAME = 'rfiSupplierDetailViewSlice';
export interface RFISupplierDetailViewState {
  vendorId: string;
  rfiRequestForm: RFIRequestForm;
  questions: RFXQuestion[];
  // When a new question is just created, we need to make sure it is created in the backend,
  // and related documents are attached. Before that, it is the pending question.
  pendingQuestion: PendingQuestionProps | null;
  status: {
    // Whether detail view page is initiating
    isInitiatingDetailView: boolean;
    // Whether supplier clicked the "send" button and back-end is processing
    isSendingMessage: boolean;
    // Whether the page is refreshing
    isRefreshingPage: boolean;
    // Whether supplier confirmed or declined or no action to participate in the RFI
    // true: participate, false: declined, null: not decided
    willParticipateInRFI: boolean | null;
  };
  errors: {
    initiatingDetailViewError: string;
    sendMessageError: string;
    refreshPageError: string;
  };
}

export interface SetRFIResponsStatusParam {
  statusKey: keyof RFISupplierDetailViewState['status'];
  status: RFISupplierDetailViewState['status'][keyof RFISupplierDetailViewState['status']];
}

export interface SetRFIResponsErrorParam {
  errorKey: keyof RFISupplierDetailViewState['errors'];
  error: RFISupplierDetailViewState['errors'][keyof RFISupplierDetailViewState['errors']];
}

const initialState: RFISupplierDetailViewState = {
  vendorId: '',
  rfiRequestForm: NEW_RFI_REQUEST_FORM,
  questions: [],
  pendingQuestion: null,
  status: {
    isInitiatingDetailView: false,
    isSendingMessage: false,
    isRefreshingPage: false,
    willParticipateInRFI: null
  },
  errors: {
    initiatingDetailViewError: '',
    sendMessageError: '',
    refreshPageError: ''
  }
};

const rfiSupplierDetailViewSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setInitializedState(state, action: PayloadAction<Partial<RFISupplierDetailViewState>>) {
      return {
        ...initialState,
        ...action.payload
      };
    },
    setVendorId(state, action: PayloadAction<string>) {
      state.vendorId = action.payload;
    },
    setQuestions(state, action: PayloadAction<RFXQuestion[]>) {
      state.questions = action.payload;
    },
    setPendingQuestion(state, action: PayloadAction<PendingQuestionProps>) {
      state.pendingQuestion = action.payload;
    },
    clearPendingQuestion(state) {
      state.pendingQuestion = null;
    },
    addQuestion(state, action) {
      state.questions = [...state.questions, action.payload];
    },
    setRFISupplierDetailViewStatus(state, action: PayloadAction<SetRFIResponsStatusParam>) {
      const { statusKey, status } = action.payload;
      return {
        ...state,
        status: {
          ...state.status,
          [statusKey]: status
        }
      };
    },
    setRFISupplierDetailViewError(state, action: PayloadAction<SetRFIResponsErrorParam>) {
      const { errorKey, error } = action.payload;
      return {
        ...state,
        errors: {
          ...state.errors,
          [errorKey]: error
        }
      };
    }
  }
});
export interface InitializeDetailViewUIInput extends UIInput {
  rfxId: string;
  username: string;
}

/**
 * Initialize RFI detail view state, which includes related RFX request, and
 * Supplier's effective vendorId.
 */
export const onInitializeSupplierDetailView = createAsyncThunk<
  void,
  InitializeDetailViewUIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(
  `${SLICE_NAME}/onInitializeSupplierDetailView`,
  async ({ apolloClient, rfxId, username }, { dispatch, getState }): Promise<void> => {
    try {
      dispatch(setRFISupplierDetailViewStatus({ statusKey: 'isInitiatingDetailView', status: true }));

      const rfxRequest = await loadRFXRequest({ apolloClient, rfxId, revision: 1 });
      const rfiRequestForm = convertRFXRequestToRFIRequestForm(rfxRequest);
      const user = await getUserByUsername({ apolloClient, username });
      const effectiveVendorId: string = getRFXRequestIncludedVendor(rfxRequest, user.vendorIds);
      const questions = await getRFXQuestionsForRequest({ apolloClient, rfxId, revision: 1 });
      const sortedQuestions = sortQuestionsByCreationTime(questions);
      dispatch(
        setInitializedState({
          rfiRequestForm,
          vendorId: effectiveVendorId,
          questions: sortedQuestions
        })
      );
    } catch (error) {
      const sanitizedError = convertSystemErrorToString(error);
      dispatch(setRFISupplierDetailViewError({ errorKey: 'initiatingDetailViewError', error: sanitizedError }));
    } finally {
      dispatch(setRFISupplierDetailViewStatus({ statusKey: 'isInitiatingDetailView', status: false }));
    }
  }
);

/**
 * Supplier send response message by clicking button.
 *
 * This will create RFXQuestion and put it in backend. It will continue to check attachments, and created them.
 *
 * Since this is an async operation, it will create a pendingQuestion in redux. The front-end will reload the questions
 * until the pendingQuestion is in the list.
 */
export const onSupplierSendMessage = createAsyncThunk<
  void,
  OnSendRFIMessageUIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(
  `${SLICE_NAME}/onSupplierSendMessage`,
  async ({ apolloClient, message, text, stagingArns }, { dispatch, getState }): Promise<void> => {
    try {
      dispatch(setRFISupplierDetailViewStatus({ statusKey: 'isSendingMessage', status: true }));

      const { vendorId, rfiRequestForm } = getState().rfiSupplierDetailView;
      const { fullRfxId } = rfiRequestForm.generalInformation;

      const question = createSupplierRFIQuestion({ rfxRequestId: fullRfxId, vendorId, message, text });
      const newDocuments = await createRFIReplyContent({ apolloClient, question, stagingArns });

      dispatch(setPendingQuestion({ question, documents: newDocuments }));
    } catch (error) {
      const sanitizedError = convertSystemErrorToString(error);
      dispatch(setRFISupplierDetailViewError({ errorKey: 'sendMessageError', error: sanitizedError }));
      dispatch(clearPendingQuestion());
    } finally {
      dispatch(setRFISupplierDetailViewStatus({ statusKey: 'isSendingMessage', status: false }));
    }
  }
);

interface OnSupplierSendParticipationDecisionUIInput extends UIInput {
  participationOption: string;
}

/**
 * Supplier send participation decision
 */
export const onSupplierSendParticipationDecision = createAsyncThunk<
  void,
  OnSupplierSendParticipationDecisionUIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(
  `${SLICE_NAME}/onSupplierSendParticipationDecision`,
  async ({ apolloClient, participationOption }, { dispatch, getState }): Promise<void> => {
    try {
      dispatch(setRFISupplierDetailViewStatus({ statusKey: 'isSendingMessage', status: true }));

      const { vendorId, rfiRequestForm } = getState().rfiSupplierDetailView;
      const { fullRfxId, rfiType } = rfiRequestForm.generalInformation;

      const question = createSupplierRFIParticipationQuestion({
        participationOption,
        rfxRequestId: fullRfxId,
        vendorId
      });

      // Load questions before put to avoid 2 supplier contacts put participation decision at the same time
      // TODO: server side solution

      const questions = await getRFXQuestionsForRequest({ apolloClient, rfxId: fullRfxId, revision: 1 });

      const nonReplyAllQuestions: RFXQuestion[] = getNonReplyAllQuestions(questions);
      if (nonReplyAllQuestions.length > 0) {
        if (rfiType === RFIType.MassCommunication) {
          // Mass communication only have one option: "Acknowledged". If it has been Ack'd, no need to
          // Ack again. And no need to show error.
          return;
        }
        dispatch(
          setRFISupplierDetailViewError({
            errorKey: 'sendMessageError',
            error: 'This RFI event has been already accepted or declined.'
          })
        );
        return;
      }

      await putRFXQuestion({ apolloClient, rfxQuestion: question });
      dispatch(setQuestions([question, ...questions]));
    } catch (error) {
      const sanitizedError = convertSystemErrorToString(error);
      dispatch(setRFISupplierDetailViewError({ errorKey: 'sendMessageError', error: sanitizedError }));
      dispatch(clearPendingQuestion());
    } finally {
      dispatch(setRFISupplierDetailViewStatus({ statusKey: 'isSendingMessage', status: false }));
    }
  }
);

/**
 * Refresh page to check whether the pending question is created, and all the pending documents are attached to the question.
 * If so, clear pending items, and reload all the questions. Otherwise, do nothing, and UI should continue re-try logic.
 */
export const onRefreshSupplierDetailView = createAsyncThunk<
  void,
  UIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(`${SLICE_NAME}/onRefreshSupplierDetailView`, async ({ apolloClient }, { dispatch, getState }): Promise<void> => {
  try {
    dispatch(setRFISupplierDetailViewStatus({ statusKey: 'isRefreshingPage', status: true }));
    const { pendingQuestion, rfiRequestForm } = getState().rfiSupplierDetailView;
    if (pendingQuestion === null) {
      return;
    }

    const { question, documents } = pendingQuestion;

    const { fullRfxId: rfxId } = rfiRequestForm.generalInformation;

    // TODO: currently, this call only load 100 messages. Need to add pagination logic to support
    // more than 100 messages.
    const allQuestions = await getRFXQuestionsForRequest({ apolloClient, rfxId, revision: 1 });

    if (hasNoPendingQuestionAndDocuments({ question, documents, allQuestions })) {
      dispatch(clearPendingQuestion());
      const sortedQuestions = sortQuestionsByCreationTime(allQuestions);
      dispatch(setQuestions(sortedQuestions));
    }
  } catch (error) {
    const sanitizedError = convertSystemErrorToString(error);
    dispatch(setRFISupplierDetailViewError({ errorKey: 'refreshPageError', error: sanitizedError }));
  } finally {
    dispatch(setRFISupplierDetailViewStatus({ statusKey: 'isRefreshingPage', status: false }));
  }
});

export const {
  setInitializedState,
  setVendorId,
  setQuestions,
  setPendingQuestion,
  clearPendingQuestion,
  addQuestion,
  setRFISupplierDetailViewStatus,
  setRFISupplierDetailViewError
} = rfiSupplierDetailViewSlice.actions;

export const selectVendorId = (state: RootState) => state.rfiSupplierDetailView.vendorId;

export const selectQuestions = (state: RootState) => state.rfiSupplierDetailView.questions;

export default rfiSupplierDetailViewSlice.reducer;
